import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { first } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { CompanyService } from '@app/_services/company.service';
import { BranchService } from '@app/_services/branch.service';
import { AccountService, AlertService } from '@app/_services';
import { Company } from '@app/_models/master/company';
import { Branch } from '@app/_models/master/branch';

@Component({
    selector: 'app-company-branch-selector',
    templateUrl: 'company-branch-selector.component.html',
    styleUrls: ['./company-branch-selector.component.css']
})
export class CompanyBranchSelectorComponent implements OnInit, OnDestroy {
    companies: Company[] = [];
    branches: Branch[] = [];
    selectedCompanyId: string = '';
    selectedBranchId: string = '';
    @Input() initialCompanyId?: string;
    @Input() initialBranchId?: string;
    
    @Output() companyChange = new EventEmitter<string>();
    @Output() branchChange = new EventEmitter<string>();
    // Emits once, after the initial company/branch resolution (including any
    // single-branch auto-selection) has settled. Parents that load data based on
    // the selection should trigger their first load from this instead of loading
    // immediately on their own ngOnInit and then again from a `branchChange` that
    // was really just the initial auto-selection, not a user action.
    @Output() selectionReady = new EventEmitter<{ companyId: string; branchId: string }>();
    private selectionReadyEmitted = false;
    private branchesRequest?: Subscription;

    constructor(
        private companyService: CompanyService,
        private branchService: BranchService,
        public accountService: AccountService,
        private alertService: AlertService
    ) { }

    ngOnInit() {
        this.loadCompanies();
    }

    ngOnDestroy() {
        this.branchesRequest?.unsubscribe();
    }

    loadCurrentSelection() {
        // Load current company/branch selection for admin users
        if (this.accountService.isAdmin()) {
            const selection = this.accountService.getCurrentCompanyBranchSelection();
            this.selectedCompanyId = this.initialCompanyId ?? selection.companyId;
            this.selectedBranchId = this.initialBranchId ?? selection.branchId;

            // For company admin, load branches for their company
            if (this.accountService.isCompanyAdmin()) {
                const user = this.accountService.userValue;
                this.selectedCompanyId = user?.companyId?.toString() || '';
            } else if (this.selectedCompanyId &&
                !this.companies.some(company => String(company.id) === this.selectedCompanyId)) {
                this.selectedCompanyId = '';
                this.selectedBranchId = '';
                this.accountService.updateCompanyBranchSelection('', '');
            }

            if (this.selectedCompanyId) {
                this.loadBranchesForCompany(true);
                return;
            }
        }

        // Nothing async left to resolve (no admin, or no company yet) - report readiness now.
        this.emitSelectionReady();
    }

    private emitSelectionReady() {
        if (this.selectionReadyEmitted) {
            return;
        }
        this.selectionReadyEmitted = true;
        this.selectionReady.emit({ companyId: this.selectedCompanyId, branchId: this.selectedBranchId });
    }

    loadCompanies() {
        // Company admins don't need to load companies - they're restricted to their own
        if (this.accountService.isCompanyAdmin()) {
            const user = this.accountService.userValue;
            if (user?.companyId) {
                // Load only their company
                this.companyService.getById(user.companyId.toString())
                    .pipe(first())
                    .subscribe({
                        next: (company) => {
                            this.companies = company ? [company] : [];
                            if (!company) {
                                this.alertService.error('Assigned company is unavailable.');
                                this.emitSelectionReady();
                                return;
                            }
                            this.loadCurrentSelection();
                        },
                        error: (error) => {
                            this.alertService.error("Error loading company:", error);
                            this.emitSelectionReady();
                        }
                    });
            }
            return;
        }

        // Super admin loads all companies
        this.companyService.getActive()
            .pipe(first())
            .subscribe({
                next: (companies) => {
                    this.companies = companies || [];
                    this.loadCurrentSelection();
                },
                error: (error) => {
                    this.alertService.error("Error loading companies:", error);
                    this.emitSelectionReady();
                }
            });
    }

    loadDefaultCompany() {
        this.companyService.getDefaultCompany()
            .pipe(first())
            .subscribe({
                next: (company) => {
                    if (company) {
                        this.selectedCompanyId = company.id!;
                        this.loadBranchesForCompany();
                        this.companyChange.emit(this.selectedCompanyId);
                    }
                },
                error: (error) => {
                    // No default company set, that's fine
                    console.log('No default company set');
                }
            });
    }

    onCompanyChange() {
        this.selectedBranchId = ''; // Reset branch selection
        this.loadBranchesForCompany();
        
        // Update the stored selection in AccountService
        this.accountService.updateCompanyBranchSelection(this.selectedCompanyId, '');
        
        this.companyChange.emit(this.selectedCompanyId);
        this.branchChange.emit('');
    }

    onBranchChange() {
        // Update the stored selection in AccountService
        this.accountService.updateCompanyBranchSelection(this.selectedCompanyId, this.selectedBranchId);
        
        this.branchChange.emit(this.selectedBranchId);
    }

    loadBranchesForCompany(isInitialLoad: boolean = false) {
        this.branchesRequest?.unsubscribe();
        if (!this.selectedCompanyId) {
            this.branches = [];
            this.emitSelectionReady();
            return;
        }

        this.branchesRequest = this.branchService.getByCompanyId(this.selectedCompanyId)
            .pipe(first())
            .subscribe({
                next: (branches) => {
                    this.branches = (branches || []).filter(branch => branch.isActive);
                    const previousBranchId = this.selectedBranchId;
                    if (this.branches.length === 1) {
                        this.selectedBranchId = String(this.branches[0].id);
                    } else if (!this.branches.some(branch => String(branch.id) === this.selectedBranchId)) {
                        this.selectedBranchId = '';
                    }
                    if (this.selectedBranchId !== previousBranchId) {
                        this.accountService.updateCompanyBranchSelection(this.selectedCompanyId, this.selectedBranchId);
                        if (!isInitialLoad && this.selectionReadyEmitted) this.branchChange.emit(this.selectedBranchId);
                    }
                    if (isInitialLoad && this.initialCompanyId !== undefined) {
                        this.accountService.updateCompanyBranchSelection(this.selectedCompanyId, this.selectedBranchId);
                    }
                    this.emitSelectionReady();
                },
                error: (error) => {
                    this.alertService.error("Error loading branches:", error);
                    this.branches = [];
                    this.selectedBranchId = '';
                    this.accountService.updateCompanyBranchSelection(this.selectedCompanyId, '');
                    this.emitSelectionReady();
                }
            });
    }
}
